import type { GossipCard } from "@/mocks/gossip";
import { SectionHead, Tag, type Tone } from "./ui";

type Props = {
  id: string;
  eyebrow: string;
  title: string;
  sub: string;
  items: GossipCard[];
  tone: Tone;
  actionLabel: string;
};

function CardMeta({ item }: { item: GossipCard }) {
  return (
    <div className="mt-5 flex items-center justify-between border-t border-background-200 pt-4">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-100 text-[10px] font-bold text-secondary-800">
          {item.author.slice(0, 1)}
        </span>
        <span className="text-xs text-foreground-600">{item.author}</span>
        <span className="text-xs text-foreground-400">· {item.readMin}分钟</span>
      </div>
      <div className="flex items-center gap-3 text-xs text-foreground-500">
        <span className="inline-flex items-center gap-1">
          <i className="ri-chat-3-line" />
          {item.discuss}
        </span>
        <span className="hidden sm:inline-flex items-center gap-1">
          <i className="ri-time-line" />
          {item.up}
        </span>
      </div>
    </div>
  );
}

export default function GossipSection({ id, eyebrow, title, sub, items, tone, actionLabel }: Props) {
  const [feature, ...rest] = items;
  const accentBtn = tone === "accent" ? "hover:bg-accent-600 bg-accent-500" : "hover:bg-primary-600 bg-primary-500";
  const hoverText = tone === "accent" ? "hover:text-accent-700" : "hover:text-primary-700";

  return (
    <section id={id} className="bg-background-50">
      <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-16">
        <SectionHead eyebrow={eyebrow} title={title} sub={sub} tone={tone} />

        {/* 头条大卡 */}
        <article className="group grid gap-0 overflow-hidden rounded-2xl border border-background-200 bg-background-50 lg:grid-cols-2">
          {feature.image ? (
            <a href="#impact" className="relative block aspect-[16/9] overflow-hidden lg:aspect-auto lg:h-full">
              <img
                src={feature.image}
                alt={`${feature.title}——刚刚爆料娱乐八卦配图`}
                className="h-full w-full object-cover object-top transition duration-700 group-hover:scale-105"
              />
              <span className="absolute left-4 top-4">
                <Tag tone={tone}>本期主打</Tag>
              </span>
            </a>
          ) : null}
          <div className="flex flex-col justify-center p-6 md:p-9">
            <div className="flex items-center gap-2">
              <Tag tone={tone}>{feature.tag}</Tag>
              <span className="text-xs text-foreground-400">{feature.up}</span>
            </div>
            <h3 className={`font-heading mt-4 text-xl font-bold leading-snug text-foreground-950 transition md:text-2xl ${hoverText}`}>
              <a href="#impact">{feature.title}</a>
            </h3>
            <p className="mt-4 text-sm leading-7 text-foreground-600">{feature.summary}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="#impact"
                className={`whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold text-background-50 transition ${accentBtn}`}
              >
                去吃这个瓜
                <i className="ri-arrow-right-line ml-1.5" />
              </a>
              <span className="inline-flex items-center gap-1.5 text-sm text-foreground-500">
                <i className="ri-message-3-line" />
                {feature.discuss} 讨论
              </span>
            </div>
          </div>
        </article>

        {/* 其余卡片 */}
        <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((item) => (
            <article
              key={item.title}
              className="flex flex-col rounded-2xl border border-background-200 bg-background-100/50 p-5 transition hover:-translate-y-1 hover:border-background-300 hover:bg-background-50"
            >
              <div className="flex items-center gap-2">
                <Tag tone={tone}>{item.tag}</Tag>
                <span className="text-xs text-foreground-400">{item.up}</span>
              </div>
              <h3 className="font-heading mt-3 text-base font-bold leading-snug text-foreground-950">
                <a href="#impact" className={`transition ${hoverText}`}>
                  {item.title}
                </a>
              </h3>
              <p className="mt-2 flex-1 text-[13px] leading-6 text-foreground-600">{item.summary}</p>
              <CardMeta item={item} />
            </article>
          ))}
        </div>

        <div className="mt-8 text-center">
          <a
            href="#news"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-700 transition hover:border-background-400 hover:text-primary-700"
          >
            {actionLabel}
            <i className="ri-arrow-right-down-line" />
          </a>
        </div>
      </div>
    </section>
  );
}